import { useEffect, useState } from "react";
import axios from "axios";
import SectionTitle from "../components/SectionTitle";
import PageSeo from "../components/PageSeo";
import GalleryModal from "../components/GalleryModal";
import { ASSETS } from "../mock";
import { API } from "../context/AuthContext";
import { Calendar, MapPin, Images } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);

export default function Eventos() {
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [galleryEvent, setGalleryEvent] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [u, p] = await Promise.all([
          axios.get(`${API}/events`, { params: { upcoming: true } }),
          axios.get(`${API}/events`, { params: { upcoming: false } }),
        ]);
        setUpcoming(Array.isArray(u.data) ? u.data : []);
        setPast(Array.isArray(p.data) ? p.data : []);
      } catch (e) {
        console.warn("Eventos: fallo al cargar desde el backend", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div>
      <PageSeo
        title="Eventos"
        description="Descubre los próximos eventos solidarios de la Asociación Pequeño Fresno y revive los momentos de nuestra historia con galerías de fotos."
        path="/eventos"
      />
      <section className="max-w-6xl mx-auto px-6">
        <SectionTitle>~ próximos eventos ~</SectionTitle>

        <div className="grid md:grid-cols-2 gap-8">
          {loading && (
            <>
              <EventSkeleton />
              <EventSkeleton />
            </>
          )}
          {!loading && upcoming.map((e, i) => (
            <article key={e.id || i} className="pf-soft rounded-3xl overflow-hidden hover-lift relative">
              <div className="aspect-[16/10] overflow-hidden bg-[#1a1f16] flex items-center justify-center">
                <img
                  src={abs(e.image_url || e.img)}
                  alt={e.title}
                  className="max-w-full max-h-full w-auto h-auto object-contain"
                  onError={(ev) => (ev.currentTarget.src = ASSETS.leafDeco)}
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-4 text-[#5c6b47] text-sm flex-wrap">
                  <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" />{e.date}</span>
                  {e.location && <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{e.location}</span>}
                </div>
                <h3 className="font-cursive text-3xl text-[#4d6a3a] mt-3">{e.title}</h3>
                <p className="text-[#3d4a30] mt-2 leading-relaxed">{e.description}</p>
              </div>
            </article>
          ))}

          {!loading && upcoming.length === 0 && (
            <div className="rounded-3xl border-2 border-dashed border-[#a5b58e] flex items-center justify-center p-10 text-center col-span-full">
              <div>
                <img src={ASSETS.butterflyLeaf} alt="" className="w-24 mx-auto opacity-80" />
                <p className="mt-4 font-cursive text-3xl text-[#4d6a3a]">Pronto más eventos</p>
                <p className="text-[#5c6b47] mt-1">¡Síguenos en Instagram!</p>
              </div>
            </div>
          )}
        </div>

        <SectionTitle className="!my-20">~ eventos pasados ~</SectionTitle>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading && (
            <>
              <EventSkeleton compact />
              <EventSkeleton compact />
              <EventSkeleton compact />
            </>
          )}
          {!loading && past.map((e, i) => {
            const gallery = e.gallery || [];
            const hasGallery = gallery.length > 0;
            return (
              <article key={e.id || i} className="pf-soft rounded-3xl overflow-hidden hover-lift group">
                <button
                  disabled={!hasGallery}
                  onClick={() => hasGallery && setGalleryEvent(e)}
                  className="block w-full text-left cursor-pointer disabled:cursor-default"
                >
                  <div className="aspect-[16/10] overflow-hidden relative bg-[#1a1f16] flex items-center justify-center">
                    <img
                      src={abs(e.image_url || e.img)}
                      alt={e.title}
                      className="max-w-full max-h-full w-auto h-auto object-contain group-hover:scale-105 transition-transform duration-500"
                      onError={(ev) => (ev.currentTarget.src = ASSETS.leafDeco)}
                    />
                    {hasGallery && (
                      <div className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 text-xs text-[#3d4a30] shadow">
                        <Images className="w-3.5 h-3.5" /> {gallery.length} fotos
                      </div>
                    )}
                  </div>
                </button>
                <div className="p-5">
                  <div className="text-xs text-[#5c6b47] inline-flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" /> {e.date}
                  </div>
                  <h3 className="font-cursive text-2xl text-[#4d6a3a] mt-2 leading-tight">{e.title}</h3>
                  <p className="text-[#3d4a30] mt-2 leading-relaxed text-sm">{e.description}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {galleryEvent && <GalleryModal event={galleryEvent} onClose={() => setGalleryEvent(null)} />}
    </div>
  );
}

function EventSkeleton({ compact = false }) {
  return (
    <div className="pf-soft rounded-3xl overflow-hidden animate-pulse" aria-hidden="true">
      <div className="aspect-[16/10] bg-[#e6ecd7]" />
      <div className={compact ? "p-5" : "p-6"}>
        <div className="h-3 w-24 bg-[#e6ecd7] rounded" />
        <div className={`${compact ? "h-6 mt-2" : "h-8 mt-3"} w-3/4 bg-[#e6ecd7] rounded`} />
        <div className="h-3 mt-3 w-full bg-[#eef2e0] rounded" />
        <div className="h-3 mt-2 w-5/6 bg-[#eef2e0] rounded" />
      </div>
    </div>
  );
}
